/**
 * Template Data Transformers
 * Functions to transform template data between different formats
 */

import {
  buildComponentsFromData,
  extractVariablesFromTemplateJson,
  getWabaAccount,
} from "./templateUtils";

/**
 * Transform duplicate template data to component format
 * Handles both API template format (with template_json) and flat format
 */
export const transformDuplicateTemplate = (duplicateData) => {
  // If it's an API template format (has template_json), extract from it
  if (duplicateData.template_json && duplicateData.template_json.components) {
    const templateJson = duplicateData.template_json;
    const components = templateJson.components || [];

    // Extract components directly from template_json
    const headerComponent = components.find(c => c.type === 'HEADER');
    const bodyComponent = components.find(c => c.type === 'BODY');
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');

    // Build components array with IDs for buttons
    const transformedComponents = [];

    // HEADER component
    if (headerComponent) {
      transformedComponents.push({
        type: "HEADER",
        format: headerComponent.format || "NONE",
        text: headerComponent.text || "",
      });
    }

    // BODY component
    if (bodyComponent) {
      transformedComponents.push({
        type: "BODY",
        text: bodyComponent.text || "",
        example: null,
      });
    }

    // FOOTER component
    if (footerComponent) {
      transformedComponents.push({
        type: "FOOTER",
        text: footerComponent.text || "",
      });
    }

    // BUTTONS component with IDs
    if (buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0) {
      const buttonsWithIds = buttonsComponent.buttons.map((btn, index) => {
        const buttonId = Date.now() + index * 10000;
        const buttonObj = {
          id: buttonId,
          type: btn.type,
          text: btn.text || "",
          url: btn.url || undefined,
          phone_number: btn.phone_number || undefined,
          example: btn.example || undefined,
        };

        // Extract URL variable if URL contains {{variable}}
        if (btn.type === "URL" && btn.url) {
          const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
          if (urlVariables.length === 1) {
            const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
            if (varMatch) {
              const sample = Array.isArray(btn.example) && btn.example.length > 0
                ? btn.example[0]
                : "";
              buttonObj.urlVariable = {
                name: varMatch[1],
                sample: sample,
              };
            }
          }
        }

        return buttonObj;
      });

      transformedComponents.push({
        type: "BUTTONS",
        buttons: buttonsWithIds,
      });
    }

    // Extract variables from template_json
    const variables = extractVariablesFromTemplateJson(templateJson);

    // Get WABA account
    const wabaAccount = getWabaAccount();

    // Get header file preview
    const headerFormat = headerComponent?.format?.toUpperCase() || "NONE";
    const headerFilePreview =
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
      duplicateData.header_media_url
        ? duplicateData.header_media_url
        : null;

    return {
      name: duplicateData.name || `copy_${duplicateData.originalName || duplicateData.name || ""}`,
      category: duplicateData.category || "UTILITY",
      language: duplicateData.language || "en_US",
      components: transformedComponents,
      wabaId: wabaAccount?.wabaId || duplicateData.waba_id || "",
      phoneId: wabaAccount?.phoneId || duplicateData.phone_id || "",
      headerFile: null,
      headerFilePreview,
      header_media_url: duplicateData.header_media_url || null,
      variables: variables,
    };
  }

  // Handle flat format (existing logic)
  const components = [];

  // HEADER component
  if (duplicateData.headerFormat && duplicateData.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: duplicateData.headerFormat,
    };
    if (duplicateData.headerText) {
      headerComponent.text = duplicateData.headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  components.push({
    type: "BODY",
    text: duplicateData.body || "",
    example: null,
  });

  // FOOTER component
  if (duplicateData.footer) {
    components.push({
      type: "FOOTER",
      text: duplicateData.footer,
    });
  }

  // BUTTONS component
  if (duplicateData.buttons && duplicateData.buttons.length > 0) {
    const buttonsWithIds = duplicateData.buttons.map((btn, index) => {
      const buttonId =
        btn.id && typeof btn.id === "number"
          ? btn.id
          : Date.now() + index * 10000;

      const buttonObj = {
        id: buttonId,
        type: btn.type,
        text: btn.text || "",
        url: btn.url || undefined,
        phone_number: btn.phone_number || undefined,
        example: btn.example || undefined,
      };

      // Extract URL variable if URL contains {{variable}}
      if (btn.type === "URL" && btn.url) {
        const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
        if (urlVariables.length === 1) {
          const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
          if (varMatch) {
            const sample =
              btn.urlVariable?.sample ||
              (Array.isArray(btn.example) && btn.example.length > 0
                ? btn.example[0]
                : "");
            buttonObj.urlVariable = {
              name: varMatch[1],
              sample: sample,
            };
          }
        } else if (urlVariables.length === 0 && btn.urlVariable) {
          buttonObj.urlVariable = btn.urlVariable;
        }
      }

      return buttonObj;
    });

    components.push({
      type: "BUTTONS",
      buttons: buttonsWithIds,
    });
  }

  // Extract variables from template_json if provided
  const variables = duplicateData.template_json
    ? extractVariablesFromTemplateJson(duplicateData.template_json)
    : [];

  // Merge with existing variables if any
  const existingVariables = duplicateData.variables || [];
  const mergedVariables = existingVariables.map((existingVar) => {
    const jsonVar = variables.find(
      (v) => v.name === existingVar.name && v.target === existingVar.target
    );
    const finalSample = jsonVar?.sample || existingVar.sample || "";
    return {
      ...existingVar,
      sample: finalSample,
    };
  });

  // Add new variables from template_json
  variables.forEach((jsonVar) => {
    const exists = mergedVariables.some(
      (v) => v.name === jsonVar.name && v.target === jsonVar.target
    );
    if (!exists) {
      mergedVariables.push(jsonVar);
    }
  });

  // Update button URL variable samples
  if (duplicateData.buttons && duplicateData.buttons.length > 0) {
    const buttonsComponent = components.find((c) => c.type === "BUTTONS");
    if (buttonsComponent && buttonsComponent.buttons) {
      const buttonUrlVariableSamples = {};
      const templateJson =
        typeof duplicateData.template_json === "string"
          ? JSON.parse(duplicateData.template_json)
          : duplicateData.template_json || {};
      const jsonButtons = templateJson.components?.find((c) => c.type === "BUTTONS");

      if (jsonButtons?.buttons) {
        jsonButtons.buttons.forEach((btn) => {
          if (btn.type === "URL" && btn.url) {
            const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
            urlVariables.forEach((match) => {
              const varNum = match.replace(/[{}]/g, "");
              const sample =
                Array.isArray(btn.example) && btn.example.length > 0
                  ? btn.example[0]
                  : "";
              if (sample) {
                buttonUrlVariableSamples[varNum] = sample;
              }
            });
          }
        });
      }

      buttonsComponent.buttons.forEach((btn) => {
        if (btn.type === "URL" && btn.urlVariable) {
          const varNum = btn.urlVariable.name;
          if (buttonUrlVariableSamples[varNum]) {
            btn.urlVariable.sample = buttonUrlVariableSamples[varNum];
          }
        }
      });
    }
  }

  // Get header file preview
  const headerFilePreview =
    ["IMAGE", "VIDEO", "DOCUMENT"].includes(duplicateData.headerFormat) &&
    duplicateData.header_media_url
      ? duplicateData.header_media_url
      : null;

  return {
    name: duplicateData.name || `copy_${duplicateData.originalName || ""}`,
    category: duplicateData.category || "UTILITY",
    language: duplicateData.language || "en_US",
    components,
    wabaId: duplicateData.wabaId || "",
    phoneId: duplicateData.phoneId || "",
    headerFile: null,
    headerFilePreview,
    header_media_url: duplicateData.header_media_url || null,
    variables: mergedVariables.length > 0 ? mergedVariables : variables,
  };
};

/**
 * Transform AI generated template to component format
 */
export const transformAiGeneratedTemplate = (generated) => {
  const components = [];

  // HEADER component
  if (generated.headerFormat && generated.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: generated.headerFormat,
    };
    if (generated.headerText) {
      headerComponent.text = generated.headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  components.push({
    type: "BODY",
    text: generated.body || "",
  });

  // FOOTER component
  if (generated.footer) {
    components.push({
      type: "FOOTER",
      text: generated.footer,
    });
  }

  // BUTTONS component
  const buttons = (generated.buttons || []).map((btn, index) => ({
    id: Date.now() + 2000 + index,
    type: btn.type,
    text: btn.text,
    url: btn.url || undefined,
    phone_number: btn.phone_number || undefined,
    example: btn.example || undefined,
  }));

  if (buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: buttons,
    });
  }

  // Extract variables
  const bodyVariables = [];
  const bodyMatches = (generated.body || "").match(/\{\{(\d+)\}\}/g) || [];
  bodyMatches.forEach((match, index) => {
    const varNum = match.replace(/[{}]/g, "");
    bodyVariables.push({
      id: Date.now() + index,
      name: varNum,
      sample: generated.variableSamples?.[`body_${varNum}`] || "",
      target: "body",
    });
  });

  const headerVariables = [];
  if (generated.headerFormat === "TEXT" && generated.headerText) {
    const headerMatches = generated.headerText.match(/\{\{(\d+)\}\}/g) || [];
    headerMatches.forEach((match, index) => {
      const varNum = match.replace(/[{}]/g, "");
      headerVariables.push({
        id: Date.now() + 1000 + index,
        name: varNum,
        sample: generated.variableSamples?.[`header_${varNum}`] || "",
        target: "header",
      });
    });
  }

  return {
    components,
    variables: [...bodyVariables, ...headerVariables],
  };
};

